import React, { useState, useEffect } from 'react';
import { getTiers, updateTier, TierConfig } from '../../api/admin';

const TierSettings: React.FC = () => {
  const [tiers, setTiers] = useState<TierConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    location_expiry_days: string;
    max_active_locations: string;
    max_downloads_per_location: string;
  }>({
    location_expiry_days: '',
    max_active_locations: '',
    max_downloads_per_location: '',
  });

  const fetchTiers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getTiers();
      // Sort tiers by a logical order
      const order = ['free', 'basic', 'premium', 'enterprise'];
      const sorted = response.tiers.sort((a, b) => {
        return order.indexOf(a.id) - order.indexOf(b.id);
      });
      setTiers(sorted);
    } catch (err) {
      setError('Failed to load tier configurations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  const handleEdit = (tier: TierConfig) => {
    setEditingTier(tier.id);
    setEditForm({
      location_expiry_days: tier.location_expiry_days?.toString() || '',
      max_active_locations: tier.max_active_locations?.toString() || '',
      max_downloads_per_location: tier.max_downloads_per_location?.toString() || '',
    });
    setSuccess('');
    setError('');
  };

  const handleCancel = () => {
    setEditingTier(null);
    setEditForm({
      location_expiry_days: '',
      max_active_locations: '',
      max_downloads_per_location: '',
    });
  };

  const handleSave = async (tierId: string) => {
    setError('');
    setSuccess('');

    try {
      await updateTier({
        tier_id: tierId,
        location_expiry_days: editForm.location_expiry_days ? parseInt(editForm.location_expiry_days) : null,
        max_active_locations: editForm.max_active_locations ? parseInt(editForm.max_active_locations) : null,
        max_downloads_per_location: editForm.max_downloads_per_location ? parseInt(editForm.max_downloads_per_location) : null,
      });
      setSuccess(`Tier "${tierId}" updated successfully`);
      setEditingTier(null);
      fetchTiers();
    } catch (err) {
      setError(`Failed to update tier: ${err}`);
    }
  };

  const getTierAccent = (tierId: string): string => {
    switch (tierId) {
      case 'free':
        return 'border-l-zinc-500';
      case 'basic':
        return 'border-l-blue-500';
      case 'premium':
        return 'border-l-purple-500';
      case 'enterprise':
        return 'border-l-amber-500';
      default:
        return 'border-l-zinc-500';
    }
  };

  const getTierTextColor = (tierId: string): string => {
    switch (tierId) {
      case 'free':
        return 'text-zinc-400';
      case 'basic':
        return 'text-blue-400';
      case 'premium':
        return 'text-purple-400';
      case 'enterprise':
        return 'text-amber-400';
      default:
        return 'text-zinc-400';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-zinc-400">Loading tier configurations...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2 text-zinc-100">Tier Configuration</h2>
        <p className="text-zinc-400 text-sm mb-4">
          Configure limits for each subscription tier. Leave fields empty for unlimited.
        </p>

        {error && (
          <div className="alert alert-error mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success mb-4">
            {success}
          </div>
        )}

        <div className="grid gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`card-dark rounded-xl p-6 border-l-4 ${getTierAccent(tier.id)}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className={`text-lg font-bold capitalize ${getTierTextColor(tier.id)}`}>
                    {tier.display_name}
                  </h3>
                  <p className="text-sm text-zinc-500">ID: {tier.id}</p>
                </div>
                {editingTier !== tier.id && (
                  <button
                    onClick={() => handleEdit(tier)}
                    className="btn-primary px-4 py-2 rounded-lg text-sm"
                  >
                    Edit
                  </button>
                )}
              </div>

              {editingTier === tier.id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Location Expiry (days)
                      </label>
                      <input
                        type="number"
                        value={editForm.location_expiry_days}
                        onChange={(e) => setEditForm({ ...editForm, location_expiry_days: e.target.value })}
                        placeholder="Unlimited"
                        min="1"
                        className="input-dark w-full px-3 py-2 rounded-lg"
                      />
                      <p className="text-xs text-zinc-500 mt-1">Days until location expires</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Max Active Locations
                      </label>
                      <input
                        type="number"
                        value={editForm.max_active_locations}
                        onChange={(e) => setEditForm({ ...editForm, max_active_locations: e.target.value })}
                        placeholder="Unlimited"
                        min="1"
                        className="input-dark w-full px-3 py-2 rounded-lg"
                      />
                      <p className="text-xs text-zinc-500 mt-1">Max locations user can have</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Max Downloads per Location
                      </label>
                      <input
                        type="number"
                        value={editForm.max_downloads_per_location}
                        onChange={(e) => setEditForm({ ...editForm, max_downloads_per_location: e.target.value })}
                        placeholder="Unlimited"
                        min="1"
                        className="input-dark w-full px-3 py-2 rounded-lg"
                      />
                      <p className="text-xs text-zinc-500 mt-1">Downloads before location is deleted</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSave(tier.id)}
                      className="btn-success px-4 py-2 rounded-lg text-sm"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancel}
                      className="btn-secondary px-4 py-2 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="text-sm text-zinc-400 font-medium">Location Expiry</div>
                    <div className="text-xl font-bold">
                      {tier.location_expiry_days === null ? (
                        <span className="text-emerald-400">Unlimited</span>
                      ) : (
                        <span className="text-zinc-200">{tier.location_expiry_days} days</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="text-sm text-zinc-400 font-medium">Max Locations</div>
                    <div className="text-xl font-bold">
                      {tier.max_active_locations === null ? (
                        <span className="text-emerald-400">Unlimited</span>
                      ) : (
                        <span className="text-zinc-200">{tier.max_active_locations}</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="text-sm text-zinc-400 font-medium">Max Downloads</div>
                    <div className="text-xl font-bold">
                      {tier.max_downloads_per_location === null ? (
                        <span className="text-emerald-400">Unlimited</span>
                      ) : (
                        <span className="text-zinc-200">{tier.max_downloads_per_location}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 alert alert-info">
          <h4 className="font-medium mb-2">How Tier Limits Work</h4>
          <ul className="text-sm space-y-1 opacity-90">
            <li><strong>Location Expiry:</strong> Locations automatically become inaccessible after this many days from creation.</li>
            <li><strong>Max Active Locations:</strong> Users cannot create new locations if they have this many active (non-expired, non-deleted) locations.</li>
            <li><strong>Max Downloads:</strong> After this many downloads, the location is automatically deleted. Users are warned on their final download.</li>
            <li><strong>Unlimited:</strong> Leave a field empty to set it as unlimited for that tier.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TierSettings;
